import { useContext, useMemo, useRef, useState } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { toCsv } from "@/utils/csv";

type Step4SelectLeadsProps = {
  selectedTemplateId: string;
  uploadedFile: File | null;
  onFileUpload: (file: File | null) => void;
};

const Step4SelectLeads = ({
  selectedTemplateId,
  uploadedFile,
  onFileUpload,
}: Step4SelectLeadsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(uploadedFile ? 3 : 1);
  const { merchantId } = useContext(MerchantContext);

  const { data, isLoading } = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  // Find the selected template
  const selectedTemplate = useMemo(() => {
    if (!data?.templates || !selectedTemplateId) return null;
    return data.templates.find((t) => t.id === selectedTemplateId);
  }, [data?.templates, selectedTemplateId]);

  // Extract parameters from template body text
  const templateParameters = useMemo(() => {
    if (!selectedTemplate?.bodyPreview) return [];

    const matches = selectedTemplate.bodyPreview.matchAll(/{{(\w+)}}/g);
    const params = Array.from(matches).map((match) => match[1]);

    // Remove duplicates
    return [...new Set(params)];
  }, [selectedTemplate]);

  const handleDownloadTemplate = () => {
    if (!selectedTemplate) return;

    const templateParamsWithoutNameAndCrmId = templateParameters.filter((p) => p !== "name" && p !== "crmId");
    // `contact` rather than `phone`: the column now takes a phone number, a
    // WhatsApp username, or an ID de WhatsApp. A file still carrying the old
    // `phone` header keeps uploading.
    const headerKeys = ["contact", "name", "crmId", ...templateParamsWithoutNameAndCrmId];
    const columns = headerKeys.map((k) => ({ key: k, header: k }));
    const csvContent = toCsv(columns, []);

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `broadcast_template_${selectedTemplate.name}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Move to step 2 after download
    setCurrentStep(2);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      onFileUpload(file);
      setCurrentStep(3);
    } else {
      alert("Por favor selecciona un archivo CSV válido");
    }
  };

  const handleRemoveFile = () => {
    onFileUpload(null);
    setCurrentStep(2);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">Cargando plantilla...</p>
        </div>
      </div>
    );
  }

  if (!selectedTemplate) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="py-8 text-center text-gray-500">
            No se pudo cargar la plantilla seleccionada.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Preparar Archivo de Leads</p>
        <p className="mb-4 text-sm text-gray-600">
          Sigue estos pasos para preparar tu envío masivo. Los mensajes se enviarán en el siguiente
          paso.
        </p>
      </div>

      <Alert status="info">
        <p className="text-sm">
          Los mensajes <strong>NO</strong> se enviarán aún. Podrás revisar y confirmar todo antes de
          enviar.
        </p>
      </Alert>

      {/* Step 1: Download Template */}
      <Card
        className={cn(
          "border-2",
          currentStep === 1 ? "border-purple-500" : currentStep > 1 ? "border-green-500" : "border-gray-200"
        )}
      >
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-3">
            <Badge
              variant="outline"
              className={cn(
                "px-2 py-1 text-base",
                currentStep > 1 ? "bg-green-100 text-green-800 border-green-200" : "bg-purple-100 text-purple-800 border-purple-200"
              )}
            >
              {currentStep > 1 ? "✓" : "1"}
            </Badge>
            <p className="font-bold">Descargar Plantilla</p>
          </div>

          <div className="flex flex-col items-start gap-3">
            <div>
              <p className="mb-2 text-sm text-gray-600">
                Plantilla seleccionada: <strong>{selectedTemplate.name}</strong>
              </p>

              <p className="mb-1 text-sm text-gray-600">Columnas del CSV:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">contact</Badge>
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">name</Badge>
                <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">crmId (opcional)</Badge>
                {templateParameters
                  .filter((param) => param !== "name" && param !== "crmId")
                  .map((param) => (
                    <Badge key={param} variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
                      {param}
                    </Badge>
                  ))}
              </div>
            </div>

            <Button
              onClick={handleDownloadTemplate}
              className="w-full"
              disabled={currentStep > 1}
            >
              {currentStep > 1 ? "✓ Plantilla Descargada" : "Descargar Plantilla"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Fill Template */}
      <Card
        className={cn(
          "border-2",
          currentStep === 2 ? "border-purple-500" : currentStep > 2 ? "border-green-500" : "border-gray-200",
          currentStep < 2 && "opacity-60"
        )}
      >
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-3">
            <Badge
              variant="outline"
              className={cn(
                "px-2 py-1 text-base",
                currentStep > 2 ? "bg-green-100 text-green-800 border-green-200" : "bg-purple-100 text-purple-800 border-purple-200"
              )}
            >
              {currentStep > 2 ? "✓" : "2"}
            </Badge>
            <p className="font-bold">Completar Información</p>
          </div>

          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-gray-600">
              Abre el archivo descargado y completa:
            </p>
            <div className="flex flex-col items-start gap-1 pl-4">
              <p className="text-sm text-gray-600">
                • <strong>contact</strong>: el teléfono con código de país (ej: 573001234567), el
                usuario de WhatsApp (ej: @anagomez) o el ID de WhatsApp
              </p>
              {/* A username only finds a lead we already have. Said here, where
                  the merchant is filling the file, rather than after the upload. */}
              <p className="pl-3 text-xs text-gray-500">
                El usuario de WhatsApp solo funciona si esa persona ya escribió a esta línea antes.
              </p>
              {templateParameters.map((param) => (
                <p key={param} className="text-sm text-gray-600">
                  • {param}
                </p>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Upload Template */}
      <Card
        className={cn(
          "border-2",
          currentStep === 3 ? "border-green-500" : "border-gray-200",
          currentStep < 2 && "opacity-60"
        )}
      >
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-3">
            <Badge
              variant="outline"
              className={cn(
                "px-2 py-1 text-base",
                currentStep === 3 ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-800 border-gray-200"
              )}
            >
              {currentStep === 3 ? "✓" : "3"}
            </Badge>
            <p className="font-bold">Subir Archivo Completado</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="w-full text-sm text-gray-600">
              Sube el archivo CSV con la información completada.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />

            {uploadedFile ? (
              <Card className="w-full border border-green-200 bg-green-50">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-green-800">
                        ✓ {uploadedFile.name}
                      </p>
                      <p className="text-xs text-green-600">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleRemoveFile}>
                      Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button
                onClick={handleSelectFile}
                className="w-full"
                disabled={currentStep < 2}
              >
                Seleccionar Archivo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Step4SelectLeads;
